import { Alert, Button } from "flowbite-react";
import { useFieldArray, useForm } from "react-hook-form";
import { ADMIN } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import SelectInput from "../inputs/SelectInput";
import { Tooltip } from "react-tooltip";
import { FaRegQuestionCircle } from "react-icons/fa";
import { formatToReal } from "../../utils/utils";

const EditQueryCustomSpecificForm = ({
  company,
  isOpen,
  query,
  setOpenModal,
  fetchCompanyQueries,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [forceRenderForm, setForceRenderForm] = useState(false);

  const { isLoading: isLoadingProviders, data: providers } = useQuery(
    ["providers", company],
    async () => {
      const { data: items } = await axios.get(`${ADMIN.GET_PROVIDERS_ALL}`);
      return items.data;
    },
    { enabled: isOpen }
  );

  const { isLoading: isLoadingQueryCustom, data: queryCustom } = useQuery(
    ["queryCustom", query],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_QUERY}/${query?.id}`
      );
      return items.data;
    },
    { enabled: isOpen }
  );

  const { isLoading: isLoadingQueryCustomized, data: queryCustomized } =
    useQuery(
      ["queryCustomized", query, company],
      async () => {
        const { data: items } = await axios.get(
          `${ADMIN.GET_QUERY_CUSTOMIZED}/${query?.id}/${company?.id}`
        );
        return items.data;
      },
      { enabled: isOpen }
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      query: {},
      queryCustomized: {},
      fields: [],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "fields",
  });
  const priorityOrderRef = useRef("");

  useEffect(() => {
    if (!fields.length) return;

    const orderMap = new Map();
    fields.forEach((field, index) => {
      orderMap.set(field.id, index);
    });

    const getPriorityValue = (field) => {
      const numericPriority = Number(field.priority);
      if (Number.isFinite(numericPriority) && numericPriority > 0) {
        return numericPriority;
      }
      return (orderMap.get(field.id) ?? 0) + 1;
    };

    const priorityKey = fields
      .map((field) => `${field.id}:${getPriorityValue(field)}`)
      .join("|");

    if (priorityOrderRef.current === priorityKey) return;

    const sortedFields = [...fields].sort((a, b) => {
      const priorityA = getPriorityValue(a);
      const priorityB = getPriorityValue(b);
      if (priorityA === priorityB) {
        return (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0);
      }
      return priorityA - priorityB;
    });

    const orderChanged = sortedFields.some(
      (field, index) => field !== fields[index]
    );
    priorityOrderRef.current = priorityKey;

    if (orderChanged) {
      replace(sortedFields);
    }
  }, [fields, replace]);

  useEffect(() => {
    const custom = queryCustomized || queryCustom?.QueryCustomized;

    if (!custom || !providers) return;

    const staticDefaults = {
      query: {
        name: query.name || "",
        price: query.price || 0,
        url: query.url || "",
        categoryId: query.categoryId?.toString() || "",
        description: query.description || "",
      },
      queryCustomized: {
        parameterType: custom.parameterType || "",
        showAlertsBoard: custom.showAlertsBoard || false,
      },
    };

    const dynamicFields = (custom.QueryCustomizedFields || []).map(
      (f, idx) => ({
        priority: f.priority ?? idx + 1,
        providerId: f.ProviderId || f.providerId,
        queryId: f.QueryId || f.queryId,
        ignoreErrors: f.ignoreErrors ?? true,
        isUniqueProvider: f.isUniqueProvider ?? false,
      })
    );

    reset(staticDefaults);
    replace(dynamicFields);
  }, [query, queryCustom, queryCustomized, providers, reset, replace]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);
    clearErrors("fields");

    const seen = new Set();
    const duplicates = [];

    data.fields.forEach((field, index) => {
      const id = String(field.queryId);
      if (id && seen.has(id)) {
        duplicates.push(index);
      }
      seen.add(id);
    });

    if (duplicates.length > 0) {
      duplicates.forEach((index) => {
        setError(`fields.${index}.queryId`, {
          type: "manual",
          message: "Conteúdo duplicado",
        });
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.put(
        `${ADMIN.GET_CUSTOMIZED_QUERY}/${query?.id}/${company?.id}`,
        data
      );
      setOpenModal(false);
      fetchCompanyQueries();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toCents = (v) => {
    if (v == null) return 0;
    const s = String(v).trim();
    const normalized = s.includes(",")
      ? s.replace(/\./g, "").replace(",", ".")
      : s;
    const n = parseFloat(normalized);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
  };

  return (
    <>
      {queryCustomized || forceRenderForm ? (
        <form
          className="mb-6 flex flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="border border-gray-300 rounded-md p-2">
            <div className="flex gap-2 justify-between items-center mb-4">
              <div className="font-semibold text-sm">Campos de retorno</div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    append({
                      priority: fields.length + 1,
                      queryId: "",
                      providerId: "",
                      ignoreErrors: true,
                    });
                  }}
                  className="rounded-full w-6 h-6 bg-primary text-white flex items-center justify-center shadow-md"
                >
                  +
                </button>
              </div>
            </div>

            {fields.map((field, index) => {
              const selectedQueryId = watch(`fields[${index}].queryId`);
              const selectedQueryContent = providers
                ?.flatMap((p) =>
                  (p.associatedQueries || []).map((q) => ({
                    providerId: p.id,
                    providerName: p.name,
                    // dados do conteúdo
                    ...q,
                    // preço específico desse provider para esse conteúdo
                    priceCents: toCents(q?.QueryProvider?.price),
                  }))
                )
                ?.filter((q) => q.id === Number(selectedQueryId));

              return (
                <div
                  key={field.id || index}
                  className={`mb-5 ${
                    index !== fields.length - 1 &&
                    "border-b border-gray-200 pb-3"
                  }`}
                >
                  <div className="flex gap-2 mb-2 items-start">
                    <div className="w-52">
                      <SelectInput
                        register={register}
                        name={`fields[${index}].priority`}
                        label="Ordem"
                        required
                      >
                        {Array.from({ length: 20 }).map((_, i) => (
                          <option key={i} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </SelectInput>
                    </div>

                    <SelectInput
                      register={register}
                      name={`fields[${index}].queryId`}
                      label="Conteúdo *"
                      required
                      error={errors?.fields?.[index]?.queryId?.message}
                    >
                      <option value="">Selecione uma consulta</option>
                      {providers
                        ?.flatMap((p) => p.associatedQueries || [])
                        ?.filter(
                          (item) => watch("query.categoryId") == item.categoryId
                        )
                        ?.map((query, idx, arr) => {
                          // evitar duplicatas pelo ID do conteúdo
                          const already = arr.findIndex(
                            (q) => q.id === query.id
                          );
                          if (already !== idx) return null;
                          return (
                            <option key={query.id} value={query.id}>
                              {query.name}
                            </option>
                          );
                        })}
                    </SelectInput>

                    <SelectInput
                      register={register}
                      name={`fields[${index}].providerId`}
                      label="Provedor *"
                      required
                    >
                      <option value="">Selecione um Provedor</option>
                      {selectedQueryContent?.map((q) => (
                        <option key={q.providerId} value={q.providerId}>
                          {q.providerId} - {q.providerName}
                          {" — "}
                          {formatToReal((q.priceCents || 0) / 100)}
                        </option>
                      ))}
                    </SelectInput>

                    {fields.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          remove(index);
                        }}
                        className="w-6 min-w-6 h-6 mt-3 rounded-full bg-red-600 text-white flex items-center justify-center"
                        title="Remover campo"
                      >
                        –
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row gap-5">
                    <div className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        id={`fields[${index}].ignoreErrors`}
                        {...register(`fields[${index}].ignoreErrors`)}
                        className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                      />
                      <label
                        htmlFor={`fields[${index}].ignoreErrors`}
                        className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Ignorar erro de retorno
                      </label>
                    </div>
                    <div className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        id={`fields[${index}].isUniqueProvider`}
                        {...register(`fields[${index}].isUniqueProvider`)}
                        className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                      />
                      <label
                        htmlFor={`fields[${index}].isUniqueProvider`}
                        className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        É provedor único?
                      </label>
                      <Tooltip
                        id="tooltip-unique-provider"
                        content={
                          <span>
                            Ao assinalar, indica que a consulta será feita{" "}
                            <br />
                            preferencialmente por um provedor específico. <br />
                            Se houver falha nessa busca, o sistema não buscará
                            <br />
                            automaticamente outro provedor disponível para{" "}
                            <br />
                            garantir o retorno dos dados.
                          </span>
                        }
                      />
                      <FaRegQuestionCircle
                        className="text-gray-400 ml-1"
                        data-tooltip-id="tooltip-unique-provider"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            className="primary"
            type="submit"
            isProcessing={isSubmitting}
            disabled={isSubmitting}
          >
            Atualizar consulta
          </Button>

          {submitError && (
            <Alert withBorderAccent color="failure">
              Ops! Ocorreu um erro ao tentar atualizar esta consulta. Tente
              novamente mais tarde.
            </Alert>
          )}
        </form>
      ) : (
        <div className="flex flex-col items-center justify-center border border-yellow-300 bg-yellow-50 rounded-md p-6">
          <p className="text-center text-gray-700 mb-4">
            Esta consulta ainda não possui uma configuração específica para este
            cliente.
          </p>
          <Button onClick={() => setForceRenderForm(true)}>
            Deseja criar uma configuração personalizada?
          </Button>
        </div>
      )}

      {(isLoadingQueryCustom || isLoadingProviders) && <DrawerLoading />}
    </>
  );
};

export default EditQueryCustomSpecificForm;
